import { Injectable } from '@angular/core';
import { CalendarioEstabelecimento } from '@features/frequencia/services/calendario.service';
import {
  InstrumentoArquivoState,
  InstrumentoCampoAdicionalConfig,
  InstrumentoFormularioAba,
  InstrumentoFormularioState,
  InstrumentoItemEducacional,
  InstrumentoRecuperacaoVinculadaState,
  InstrumentoResumoView,
  InstrumentoTurmaOption
} from '../models/instrumento-avaliacao.types';
import {
  InstrumentoAvaliacaoRegistro,
  InstrumentoConfiguracaoEstabelecimento,
  InstrumentoPeriodoOption
} from './instrumento-avaliacao.service';
import { createInstrumentoFormulario } from '../utils/instrumento-avaliacao.utils';

@Injectable({
  providedIn: 'root'
})
export class InstrumentosAvaliacaoFacadeService {
  instrumentoVisualizadoId: number | null = null;
  instrumentoFormularioAberto = false;
  instrumentoAbaAtiva: InstrumentoFormularioAba = 'informacoes';
  instrumentoSalvando = false;
  instrumentoCarregando = false;
  instrumentoPublicandoId: number | null = null;
  instrumentoMostrarErros = false;
  instrumentoDescricoesRecentes: Array<{ descricao: string }> = [];
  instrumentoPeriodos: InstrumentoPeriodoOption[] = [];
  instrumentoTurmasDisponiveis: InstrumentoTurmaOption[] = [];
  instrumentoItensDisponiveis: InstrumentoItemEducacional[] = [];
  instrumentoContextoRegistros: InstrumentoAvaliacaoRegistro[] = [];
  instrumentosTelaRegistros: InstrumentoAvaliacaoRegistro[] = [];
  instrumentoCalendario: CalendarioEstabelecimento | null = null;
  instrumentoConfiguracaoEstabelecimento: InstrumentoConfiguracaoEstabelecimento | null = null;
  instrumentoConfiguracaoTurma: Record<string, unknown> | null = null;
  instrumentoEncerramentosTurma: Array<Record<string, unknown>> = [];
  instrumentoForm: InstrumentoFormularioState = createInstrumentoFormulario();
  instrumentoArquivos: InstrumentoArquivoState[] = [];
  instrumentoCampoAdicional: InstrumentoCampoAdicionalConfig = null;
  instrumentoCamposAdicionaisCarregando = false;
  instrumentoAnexosEnviando = false;
  instrumentoConfirmacao: {
    aberta: boolean;
    tipo: 'excluir' | 'publicacao';
    instrumento: InstrumentoResumoView | null;
    publicar: boolean;
    titulo: string;
    confirmadoExclusao: boolean;
  } = this.createConfirmacaoPadrao();
  instrumentoRecuperacaoVinculada: InstrumentoRecuperacaoVinculadaState = this.createRecuperacaoVinculadaPadrao();

  abrirCadastro(contexto: { turmaId: number | null; itemEducacionalId: number | null; periodoAvaliativoId: number | null }): void {
    this.instrumentoMostrarErros = false;
    this.instrumentoFormularioAberto = true;
    this.instrumentoAbaAtiva = 'informacoes';
    this.instrumentoCarregando = true;
    this.instrumentoForm = createInstrumentoFormulario(contexto);
    this.instrumentoArquivos = [];
  }

  abrirEdicao(): void {
    this.instrumentoMostrarErros = false;
    this.instrumentoFormularioAberto = true;
    this.instrumentoAbaAtiva = 'informacoes';
    this.instrumentoCarregando = true;
  }

  fecharFormulario(): void {
    this.instrumentoFormularioAberto = false;
    this.instrumentoAbaAtiva = 'informacoes';
    this.instrumentoMostrarErros = false;
    this.instrumentoCarregando = false;
  }

  selecionarDescricaoRecente(descricao: string): void {
    this.instrumentoForm.descricao = descricao || '';
  }

  abrirConfirmacaoExclusao(instrumento: InstrumentoResumoView): void {
    this.instrumentoConfirmacao = {
      aberta: true,
      tipo: 'excluir',
      instrumento,
      publicar: false,
      titulo: 'Confirmar exclusão do instrumento de avaliação',
      confirmadoExclusao: false
    };
  }

  abrirConfirmacaoPublicacao(instrumento: InstrumentoResumoView, publicar: boolean, titulo: string): void {
    this.instrumentoConfirmacao = {
      aberta: true,
      tipo: 'publicacao',
      instrumento,
      publicar,
      titulo,
      confirmadoExclusao: false
    };
  }

  fecharConfirmacao(): void {
    this.instrumentoConfirmacao = this.createConfirmacaoPadrao();
  }

  abrirRecuperacaoVinculada(instrumentoBase: InstrumentoAvaliacaoRegistro, instrumentoRecuperacao: InstrumentoAvaliacaoRegistro, adicionarNovo: boolean): void {
    this.instrumentoRecuperacaoVinculada = {
      aberta: true,
      carregando: false,
      salvando: false,
      adicionarNovo,
      instrumentoBase,
      instrumentoRecuperacao
    };
  }

  fecharRecuperacaoVinculada(): void {
    this.instrumentoRecuperacaoVinculada = this.createRecuperacaoVinculadaPadrao();
  }

  selecionarInstrumentoVisualizado(instrumentoId: number | null): void {
    this.instrumentoVisualizadoId = instrumentoId;
  }

  obterInstrumentoVisualizadoResumo(instrumentos: InstrumentoResumoView[]): InstrumentoResumoView | null {
    if (!instrumentos.length) {
      return null;
    }

    return instrumentos.find((instrumento) => instrumento.id === this.instrumentoVisualizadoId) || instrumentos[0] || null;
  }

  obterIndiceInstrumentoVisualizado(instrumentos: InstrumentoResumoView[]): number {
    const instrumentoAtual = this.obterInstrumentoVisualizadoResumo(instrumentos);
    if (!instrumentos.length || !instrumentoAtual) {
      return -1;
    }

    return instrumentos.findIndex((instrumento) => instrumento.id === instrumentoAtual.id);
  }

  selecionarInstrumentoAnterior(instrumentos: InstrumentoResumoView[]): void {
    const indiceAtual = this.obterIndiceInstrumentoVisualizado(instrumentos);
    if (indiceAtual <= 0) {
      return;
    }

    this.instrumentoVisualizadoId = instrumentos[indiceAtual - 1].id;
  }

  selecionarProximoInstrumento(instrumentos: InstrumentoResumoView[]): void {
    const indiceAtual = this.obterIndiceInstrumentoVisualizado(instrumentos);
    if (indiceAtual < 0 || indiceAtual >= instrumentos.length - 1) {
      return;
    }

    this.instrumentoVisualizadoId = instrumentos[indiceAtual + 1].id;
  }

  private createConfirmacaoPadrao() {
    return {
      aberta: false,
      tipo: 'excluir' as const,
      instrumento: null,
      publicar: false,
      titulo: '',
      confirmadoExclusao: false
    };
  }

  private createRecuperacaoVinculadaPadrao(): InstrumentoRecuperacaoVinculadaState {
    return {
      aberta: false,
      carregando: false,
      salvando: false,
      adicionarNovo: false,
      instrumentoBase: null,
      instrumentoRecuperacao: null
    };
  }

  /**
   * Zera todo o estado da facade. Chamar no `ngOnDestroy` dos hosts (desempenho-escolar,
   * instrumentos-avaliacao) — sem isso, por ser singleton (`providedIn: 'root'`), o estado
   * de formulário/anexos/confirmação de uma tela vaza pra outra ao navegar entre elas.
   */
  reset(): void {
    this.instrumentoVisualizadoId = null;
    this.instrumentoFormularioAberto = false;
    this.instrumentoAbaAtiva = 'informacoes';
    this.instrumentoSalvando = false;
    this.instrumentoCarregando = false;
    this.instrumentoPublicandoId = null;
    this.instrumentoMostrarErros = false;
    this.instrumentoDescricoesRecentes = [];
    this.instrumentoPeriodos = [];
    this.instrumentoTurmasDisponiveis = [];
    this.instrumentoItensDisponiveis = [];
    this.instrumentoContextoRegistros = [];
    this.instrumentosTelaRegistros = [];
    this.instrumentoCalendario = null;
    this.instrumentoConfiguracaoEstabelecimento = null;
    this.instrumentoConfiguracaoTurma = null;
    this.instrumentoEncerramentosTurma = [];
    this.instrumentoForm = createInstrumentoFormulario();
    this.instrumentoArquivos = [];
    this.instrumentoCampoAdicional = null;
    this.instrumentoCamposAdicionaisCarregando = false;
    this.instrumentoAnexosEnviando = false;
    this.instrumentoConfirmacao = this.createConfirmacaoPadrao();
    this.instrumentoRecuperacaoVinculada = this.createRecuperacaoVinculadaPadrao();
  }
}
